import {
  useState,
  useEffect,
  useRef,
} from "react";
import {
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";
import {
  LayoutDashboard,
  Wrench,
  Package,
  Building2,
  Briefcase,
  FileBarChart,
  ScrollText,
  Users,
  Search,
  LogOut,
  Menu,
  X,
  HardHat,
  Settings,
  Gauge,
  Moon,
  Sun,
} from "lucide-react";

import { api } from "@/lib/api";
import {
  useAuth,
  canManage,
  canManageUsers,
  hasLicenseFeature,
  hasMenuAccess,
  isAdmin,
} from "@/context/AuthContext";
import {
  RoleBadge,
  StatusBadge,
} from "@/components/StatusBadge";
import { cn } from "@/lib/utils";
import { UpdateBanner } from "@/components/UpdateNotice";
import BrandLogo from "@/components/BrandLogo";


const NAV = [
  {
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    key: "dash",
  },
  {
    to: "/equipment",
    label: "Equipment",
    icon: HardHat,
    key: "eq",
  },
  {
    to: "/maintenance",
    label: "Maintenance",
    icon: Wrench,
    key: "mnt",
  },
  {
    to: "/calibration",
    label: "Calibration",
    icon: Gauge,
    key: "cal",
  },
  {
    to: "/inventory",
    label: "Parts & Consumables",
    icon: Package,
    key: "inv",
  },
  {
    to: "/clients",
    label: "Clients",
    icon: Building2,
    key: "cli",
  },
  {
    to: "/jobs",
    label: "Jobs",
    icon: Briefcase,
    key: "job",
  },
  {
    to: "/reports",
    label: "Reports",
    icon: FileBarChart,
    key: "rep",
  },
  {
    to: "/import",
    label: "Excel Import",
    icon: ScrollText,
    key: "imp",
    manage: true,
  },
  {
    to: "/audit",
    label: "Audit Trail",
    icon: ScrollText,
    key: "aud",
    feature: "audit_log",
  },
  {
    to: "/users",
    label: "Users",
    icon: Users,
    key: "usr",
    userManager: true,
  },
  {
    to: "/settings",
    label: "Settings",
    icon: Settings,
    key: "set",
    admin: true,
  },
];


function GlobalSearch() {
  const [q, setQ] =
    useState("");
  const [res, setRes] =
    useState(null);
  const [open, setOpen] =
    useState(false);
  const boxRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!q || q.length < 1) {
      setRes(null);
      return;
    }

    const timer =
      setTimeout(async () => {
        try {
          const { data } =
            await api.get(
              `/search?q=${encodeURIComponent(
                q
              )}`
            );

          setRes(data);
          setOpen(true);
        } catch {}
      }, 220);

    return () =>
      clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const handler = (event) => {
      if (
        boxRef.current &&
        !boxRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handler
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handler
      );
  }, []);

  const go = (path) => {
    setOpen(false);
    setQ("");
    nav(path);
  };

  const hasResults =
    res &&
    (res.equipment?.length ||
      res.jobs?.length ||
      res.clients?.length);

  return (
    <div
      className="relative w-full max-w-xl"
      ref={boxRef}
    >
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

        <input
          value={q}
          onChange={(event) =>
            setQ(
              event.target.value
            )
          }
          onFocus={() =>
            res && setOpen(true)
          }
          placeholder="Search Asset/SAP, Serial/Mfg, Equipment, Job ID, Field Name, Client…"
          className="w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 max-h-[70vh] w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {!hasResults && (
            <div className="px-4 py-3 text-sm text-slate-500">
              No results
            </div>
          )}

          {res?.equipment?.length >
            0 && (
            <div className="py-1">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Equipment
              </div>

              {res.equipment.map(
                (equipment) => (
                  <button
                    key={
                      equipment.id
                    }
                    type="button"
                    onClick={() =>
                      go(
                        `/equipment/${equipment.id}`
                      )
                    }
                    className="flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {equipment.name ||
                          equipment.category}
                      </div>
                      <div className="font-mono text-xs text-slate-500">
                        SAP{" "}
                        {
                          equipment.sap_no
                        }{" "}
                        · Mfg{" "}
                        {equipment.mfg_no ||
                          "—"}
                      </div>
                    </div>

                    <StatusBadge
                      value={
                        equipment.placement
                      }
                    />
                  </button>
                )
              )}
            </div>
          )}

          {res?.jobs?.length > 0 && (
            <div className="border-t border-slate-100 py-1">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Jobs
              </div>

              {res.jobs.map(
                (job) => (
                  <button
                    key={job.id}
                    type="button"
                    onClick={() =>
                      go(
                        `/jobs/${job.id}`
                      )
                    }
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                  >
                    <span className="shrink-0 font-mono text-xs text-blue-600">
                      {
                        job.job_number
                      }
                    </span>
                    <span className="min-w-0 truncate text-sm text-slate-800">
                      {job.field_name ||
                        job.job_name ||
                        "—"}
                    </span>
                  </button>
                )
              )}
            </div>
          )}

          {res?.clients?.length >
            0 && (
            <div className="border-t border-slate-100 py-1">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Clients
              </div>

              {res.clients.map(
                (client) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() =>
                      go("/clients")
                    }
                    className="flex w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                  >
                    {client.name}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function AppLayout({
  children,
}) {
  const {
    user,
    license,
    logout,
  } = useAuth();
  const loc = useLocation();

  const [
    mobileOpen,
    setMobileOpen,
  ] = useState(false);

  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") {
      return "light";
    }

    return (
      window.localStorage.getItem("amt-theme") ||
      "light"
    );
  });

  useEffect(() => {
    const dark = theme === "dark";

    document.documentElement.classList.toggle(
      "dark",
      dark
    );

    window.localStorage.setItem(
      "amt-theme",
      theme
    );
  }, [theme]);

  const toggleTheme = () => {
    setTheme((current) =>
      current === "dark"
        ? "light"
        : "dark"
    );
  };

  const items = NAV.filter(
    (item) =>
      (!item.admin ||
        isAdmin(user)) &&
      (!item.userManager ||
        canManageUsers(user)) &&
      (!item.manage ||
        canManage(user)) &&
      (!item.feature ||
        hasLicenseFeature(
          license,
          item.feature
        )) &&
      hasMenuAccess(user, item.key)
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 flex-col overflow-y-auto border-r border-slate-200 bg-white transition-transform lg:static lg:flex lg:translate-x-0",
          mobileOpen
            ? "flex translate-x-0"
            : "hidden -translate-x-full"
        )}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="min-w-0 flex-1">
            <BrandLogo
              fallback="/amt-mark.png"
              alt="AMT - Asset Maintenance Tracker"
              className="h-auto w-[180px] max-w-full object-contain object-left"
            />
          </div>

          <button
            className="lg:hidden"
            type="button"
            onClick={() =>
              setMobileOpen(false)
            }
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="shrink-0 space-y-0.5 p-3">
          {items.map((item) => {
            const active =
              item.to === "/"
                ? loc.pathname ===
                  "/"
                : loc.pathname.startsWith(
                    item.to
                  );

            return (
              <Link
                key={item.key}
                to={item.to}
                onClick={() =>
                  setMobileOpen(false)
                }
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  active
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-slate-200 p-3">
          <div className="flex items-center gap-3 rounded-md px-2 py-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
              {(user?.name || "?")
                .slice(0, 1)
                .toUpperCase()}
            </div>

            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-900">
                {user?.name}
              </div>

              <RoleBadge
                role={user?.role}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={toggleTheme}
            className="mt-2 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
            title={
              theme === "dark"
                ? "Switch to Light Mode"
                : "Switch to Dark Mode"
            }
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
            {theme === "dark"
              ? "Light Mode"
              : "Dark Mode"}
          </button>

          <button
            type="button"
            onClick={logout}
            className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() =>
            setMobileOpen(false)
          }
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-20 flex items-center gap-2 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
          <button
            className="lg:hidden"
            type="button"
            onClick={() =>
              setMobileOpen(true)
            }
          >
            <Menu className="h-5 w-5" />
          </button>

          <GlobalSearch />

          <div className="ml-auto hidden items-center gap-2 text-xs text-slate-400 xl:flex">
            <span>by</span>
            <img
              src="/logisource-light.webp"
              alt="LogiSource Digital"
              className="h-5 w-auto object-contain opacity-70"
            />
          </div>
        </header>

        <UpdateBanner />

        <main className="flex-1 overflow-x-hidden p-4 animate-fade-in sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
